import { jsPDF } from "jspdf";
import { Region, EnvironmentalData } from "@/services/environmentalDataService";
import { pdfColors } from "./colors";
import { addDeforestationSection } from "./sectionGenerators/deforestationSection";
import { addLandLossSection } from "./sectionGenerators/landLossSection";
import { addDesertificationSection } from "./sectionGenerators/desertificationSection";
import { addEndangeredSpeciesSection } from "./sectionGenerators/endangeredSpeciesSection";

/**
 * Add title and region information to the PDF document (Title Page)
 */
export const addDocumentHeader = (doc: jsPDF, region: Region): void => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Rule 5: Title Block
  // H1 Style: Bold, Uppercase, Largest
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(
    pdfColors.primary[0],
    pdfColors.primary[1],
    pdfColors.primary[2],
  );
  doc.text("ENVIRONMENTAL STATUS REPORT", pageWidth / 2, 40, {
    align: "center",
  });

  // Geographic scope
  doc.setFontSize(18);
  doc.text(region.name.toUpperCase(), pageWidth / 2, 55, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`${region.country} • ${region.continent}`, pageWidth / 2, 65, {
    align: "center",
  });

  doc.setTextColor(pdfColors.text[0], pdfColors.text[1], pdfColors.text[2]);
  doc.setFontSize(11);
  doc.text(
    `Report generated on: ${new Date().toLocaleDateString()}`,
    pageWidth / 2,
    80,
    { align: "center" },
  );

  // Rule 5: Methodology Note
  const label = "Methodology";
  const description =
    "This report synthesizes multi-source satellite imagery, climate station data, and local environmental records integrated with carbon accounting frameworks and ESG modeling.";

  const yPos = 90;
  const margin = 20; // Distance from page edges
  const maxWidth = pageWidth - margin * 2;

  // 1. Render the Header (Bold)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80); // Slightly darker for the header
  doc.text(label, pageWidth / 2, yPos, { align: "center" });

  // 2. Render the Description (Italic & Wrapped)
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9); // Slightly smaller for the body text
  doc.setTextColor(120, 120, 120);

  // Split the text so it doesn't run off the page
  const wrappedText = doc.splitTextToSize(description, maxWidth);
  doc.text(wrappedText, pageWidth / 2, yPos + 7, { align: "center" });

  // Draw line separator
  doc.setDrawColor(
    pdfColors.primary[0],
    pdfColors.primary[1],
    pdfColors.primary[2],
  );
  doc.setLineWidth(0.5);
  doc.line(40, 120, pageWidth - 40, 120);

  doc.setFont("helvetica", "normal");
};;

/**
 * Add page footer with page numbering to the PDF document
 */
export const addPageFooters = (doc: jsPDF): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);

    // Rule 9: Standardized footer
    const footerText = `My!hu Environmental Intelligence Platform. Conditions & Trends — Regional Analysis  |  Page ${i} of ${pageCount}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 12, {
      align: "center",
    });

    // Draw horizontal line above footer for consistent placement
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.1);
    doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);
  }
};

/**
 * Returns the Y position for the next section based on the last auto table position
 */
export const getNextSectionY = (doc: jsPDF, defaultY: number = 140): number => {
  return doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 15 : defaultY;
};
